import { useState } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Filter, AlertTriangle, GitBranch } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { CheckoutFilterSelect, matchesCheckout, type CheckoutFilter } from '../workitems/CheckoutBadge';
import { useResolvedWorkflow } from '../../hooks/useResolvedWorkflow';
import { useAppStore } from '../../store/useAppStore';
import { useAuthStore } from '../../store/useAuthStore';
import KanbanCard from './KanbanCard';
import type { WorkItemType } from '../../types';

const WORK_ITEM_TYPES: { id: WorkItemType; label: string; color: string }[] = [
  { id: 'epic', label: 'Epic', color: '#8b5cf6' },
  { id: 'feature', label: 'Feature', color: '#3b82f6' },
  { id: 'story', label: 'Story', color: '#10b981' },
  { id: 'task', label: 'Task', color: '#f59e0b' },
  { id: 'bug', label: 'Bug', color: '#ef4444' },
  { id: 'security', label: 'Security', color: '#f97316' },
];

interface Props {
  /** Set when the board is scoped to one project, so its workflow resolves. */
  projectId?: string;
}

export default function KanbanBoard({ projectId }: Props = {}) {
  const { items, updateStatus, reorderItem } = useWorkItemStore();
  const { setDetailItemId } = useAppStore();
  const { canEditWorkItem } = useAuthStore();
  const isDragEnabled = canEditWorkItem();
  const [activeTypes, setActiveTypes] = useState<Set<WorkItemType>>(new Set(WORK_ITEM_TYPES.map((t) => t.id)));
  const [showDepsWarnings, setShowDepsWarnings] = useState(true);
  const [checkoutFilter, setCheckoutFilter] = useState<CheckoutFilter>('');
  const me = useAuthStore((s) => s.user?.email?.toLowerCase());

  // Only pass a work item type when exactly one is shown: a project may bind
  // a different workflow per type, so asking with several active would give
  // columns that fit none of them.
  const soleType = activeTypes.size === 1 ? [...activeTypes][0] : undefined;
  const { workflow, columns: statuses, allowedFrom, isFallback } =
    useResolvedWorkflow(projectId, soleType);

  const toggleType = (typeId: WorkItemType) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      next.has(typeId) ? next.delete(typeId) : next.add(typeId);
      return next;
    });
  };

  // Normalize legacy user_story → story, then filter by active types
  const normalizeType = (t: string): WorkItemType => (t === 'user_story' ? 'story' : t) as WorkItemType;
  const filteredItems = items.filter((item) => activeTypes.has(normalizeType(item.workItemType))
    && matchesCheckout(item.checkout, checkoutFilter, me));

  // Build dependency lookup for warnings
  const itemMap = new Map(items.map((i) => [i._id, i]));

  const hasDependencyWarning = (itemId: string): string | null => {
    if (!showDepsWarnings) return null;
    const item = itemMap.get(itemId);
    if (!item || !item.dependencies?.length) return null;
    for (const depId of item.dependencies) {
      const dep = itemMap.get(depId);
      if (dep) {
        const depStatusIdx = statuses.findIndex((s) => s.id === dep.status);
        const itemStatusIdx = statuses.findIndex((s) => s.id === item.status);
        if (depStatusIdx < itemStatusIdx) {
          return `Dependency "${dep.title}" is not yet at this stage`;
        }
      }
    }
    return null;
  };

  // Set of all known status ids for the fallback check
  const knownStatusIds = new Set(statuses.map((s) => s.id));

  const getItemsForStatus = (statusId: string, isFirstColumn: boolean) =>
    filteredItems
      .filter((item) =>
        item.status === statusId ||
        // Items with unrecognized status fall into the first column
        (isFirstColumn && !knownStatusIds.has(item.status))
      )
      .sort((a, b) => a.order - b.order);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { draggableId, source, destination } = result;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const newStatus = destination.droppableId;
    const newOrder = destination.index;
    const changingColumn = source.droppableId !== destination.droppableId;

    // Fail fast so an illegal drop need not round-trip. The API validates
    // regardless; this exists only to explain immediately.
    if (changingColumn && allowedFrom) {
      const legal = allowedFrom(source.droppableId);
      if (!legal.includes(newStatus)) {
        const labels = legal
          .map((id) => statuses.find((c) => c.id === id)?.label || id)
          .join(', ');
        toast.error(
          legal.length
            ? 'Not allowed by "' + (workflow?.name || 'workflow') +
              '". From here you can move to: ' + labels + '.'
            : '"' + (workflow?.name || 'workflow') +
              '" allows no moves out of this column.',
        );
        return;
      }
    }

    const atLimit = columnAtLimit(newStatus);
    if (changingColumn && atLimit &&
        !window.confirm(atLimit + ' is at its WIP limit. Move anyway?')) {
      return;
    }

    try {
      if (changingColumn) {
        await updateStatus(draggableId, newStatus);
      }
      await reorderItem(draggableId, newOrder, newStatus);
    } catch (e: any) {
      if (e?.awaitingApproval) {
        // Not a failure: the move was filed for approval (EP-26 FE-7) and happens
        // when enough holders approve it. The card stays until then.
        const a = e.awaitingApproval;
        toast(`Awaiting approval ${a.approvals}/${a.required} — needs ${a.permission}.`, { icon: '⏳' });
        return;
      }
      // The card springs back on its own because the store never updated.
      // Say why: the workflow's 409 carries the legal targets.
      const allowed: string[] = e?.detail?.allowed || [];
      const labels = allowed
        .map((id) => statuses.find((c) => c.id === id)?.label || id)
        .join(', ');
      toast.error(
        allowed.length
          ? e.message + ' You can move it to: ' + labels + '.'
          : (e?.message || 'Could not move that item.'),
      );
    }
  };

  /** Column label when it is already at or over its WIP limit, else ''. */
  function columnAtLimit(statusId: string): string {
    const column = statuses.find((c) => c.id === statusId);
    if (!column?.wipLimit) return '';
    const count = filteredItems.filter((i) => i.status === statusId).length;
    return count >= column.wipLimit ? column.label : '';
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter Bar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
        <Filter size={14} className="text-gray-400" />
        <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Type:</span>
        {WORK_ITEM_TYPES.map((wt) => (
          <button
            key={wt.id}
            onClick={() => toggleType(wt.id)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
              activeTypes.has(wt.id)
                ? 'text-white'
                : 'text-gray-400 bg-gray-100 dark:bg-gray-700 opacity-50'
            }`}
            style={activeTypes.has(wt.id) ? { backgroundColor: wt.color } : undefined}
          >
            {wt.label}
          </button>
        ))}
        <CheckoutFilterSelect value={checkoutFilter} onChange={setCheckoutFilter} />
        <div className="ml-auto flex items-center gap-2">
          {workflow && (
            <span
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 dark:text-gray-400"
              title={`Columns and legal moves come from this workflow (v${workflow.version})`}
            >
              <GitBranch size={12} />
              {workflow.name}
              {activeTypes.size !== 1 && (
                <span className="text-gray-400"> · all types</span>
              )}
            </span>
          )}
          {isFallback && (
            <span
              className="px-2 py-1 rounded text-xs text-amber-600 dark:text-amber-400"
              title="No workflow resolved — showing the legacy status list. Seed workflows to enable validation."
            >
              no workflow
            </span>
          )}
          <button
            onClick={() => setShowDepsWarnings(!showDepsWarnings)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
              showDepsWarnings
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <AlertTriangle size={12} />
            Dependency Alerts
          </button>
        </div>
      </div>

      {/* Board */}
      <div className="flex flex-1 overflow-x-auto p-4 gap-4">
        <DragDropContext onDragEnd={handleDragEnd}>
        {statuses.map((status, idx) => {
          const columnItems = getItemsForStatus(status.id, idx === 0);
          return (
            <div
              key={status.id}
              className={`flex flex-col shrink-0 w-72 rounded-xl ${
                status.wipLimit && columnItems.length > status.wipLimit
                  ? 'bg-red-50 dark:bg-red-900/10 ring-1 ring-red-300 dark:ring-red-800'
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}
            >
              {/* Column header */}
              <div className="flex items-center gap-2 px-3 py-3 shrink-0">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: status.color }}
                />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{status.label}</h3>
                <span
                  className={`text-xs ml-auto ${
                    status.wipLimit && columnItems.length > status.wipLimit
                      ? 'text-red-500 font-semibold'
                      : status.wipLimit && columnItems.length === status.wipLimit
                      ? 'text-amber-500 font-medium'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                  title={status.wipLimit ? `WIP limit ${status.wipLimit}` : undefined}
                >
                  {columnItems.length}{status.wipLimit ? ` / ${status.wipLimit}` : ''}
                </span>
              </div>

              {/* Droppable area */}
              <Droppable droppableId={status.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[100px] ${
                      snapshot.isDraggingOver ? 'bg-accent/5 rounded-b-xl' : ''
                    }`}
                  >
                    {columnItems.map((item, index) => (
                      <Draggable key={item._id} draggableId={item._id} index={index} isDragDisabled={!isDragEnabled}>
                        {(provided, snapshot) => {
                          const depWarning = hasDependencyWarning(item._id);
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setDetailItemId(item._id)}
                              className="relative"
                            >
                              {depWarning && (
                                <div className="absolute -top-1 -right-1 z-10" title={depWarning}>
                                  <AlertTriangle size={14} className="text-amber-500" />
                                </div>
                              )}
                              <KanbanCard item={item} isDragging={snapshot.isDragging} />
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
        </DragDropContext>
      </div>
    </div>
  );
}
